import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';

/**
 * Data structure for TeamListRow component
 */
export interface TeamListRowData {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
}

/**
 * TeamListRow Component
 *
 * Displays a team's information in a horizontal row format for list views.
 * Shows color dot, icon, name, description, and action icons.
 *
 * @example
 * ```html
 * <zag-team-list-row
 *   [team]="teamData"
 *   [editRoute]="['/teams', teamData.id]"
 *   (rowClick)="onTeamSelected($event)"
 *   (deleteClick)="onDeleteTeam($event)">
 * </zag-team-list-row>
 * ```
 */
@Component({
  selector: 'zag-team-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-team-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <div class="zag-team-list-row__content">
      <div
        class="zag-team-list-row__icon-avatar zag-icon-surface-gradient"
        [class.zag-team-list-row__icon-avatar--fallback]="!team().color"
        [style.background-color]="team().color || null"
      >
        <span class="zag-team-list-row__icon" [zagIcon]="team().icon || 'groups'" [style.color]="getIconColor(team().color)"></span>
      </div>
      <div class="zag-team-list-row__info">
        <span class="zag-team-list-row__name">{{ team().name }}</span>
        @if (team().description) {
          <span class="zag-team-list-row__description">{{ team().description }}</span>
        }
      </div>
    </div>

    <div class="zag-team-list-row__actions">
      @if (showDelete()) {
        <button
          type="button"
          class="zag-team-list-row__action zag-team-list-row__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="deleteAriaLabel() || null"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .zag-team-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-team-list-row__content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        min-width: 0;
      }

      .zag-team-list-row__icon {
        font-size: var(--entity-avatar-icon-size-list);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .zag-team-list-row__icon-avatar {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .zag-team-list-row__icon-avatar--fallback {
        background: linear-gradient(135deg, var(--color-primary), var(--color-primary-variant));
      }

      .zag-team-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-team-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-team-list-row__description {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-team-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-team-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
            color: var(--text-color-secondary);
          }
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-team-list-row__action--delete {
        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }
      }
    `,
  ],
})
export class TeamListRow {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The team data to display */
  readonly team = input.required<TeamListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(true);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the row is clicked */
  readonly rowClick = output<TeamListRowData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<TeamListRowData>();

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.team());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.team());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
